import React, { useState, useEffect, useContext } from "react";
import ListLocationTypes from "./list-location-types";
import ListSvgIcons from "./list-svg-icons";
import MultipleChoice from "../../core/ui/multiple-choice";
import SessionContext from "SessionContext";
import LocationRebuildNotice from "../../core/ui/location-rebuild-notice";
import { Alert } from 'reactstrap';
import isEqual from "lodash.isequal";

const CompanySelector = (props) => {
  const session = useContext(SessionContext);
  const {
    list,
    locationType,
    deleteLocationType,
    editLocationType,
    svgIcons = false,
    hasChanged,
    onCompanySelect = () => {},
  } = props;

  const [showList, setShowList] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [displayCompaniesList, setDisplayCompaniesList] = useState(list);
  const [selectedCompanyRadiusInfo, setSelectedCompanyRadiusInfo] = useState(null);
  const [defaultRadius, setDefaultRadius] = useState();
  const [radiusChoices, setRadiusChoices] = useState([]);
  const [editChoices, setEditChoices] = useState(false);
  const [hasUpdates, setHasUpdates] = useState(false);
  const [successLabel, setSuccessLabel] = useState("Company Default Radius Updated.");
  const [disableUpdate, setDisableUpdate] = useState(false);
  const [showRebuildNotice, setShowRebuildNotice] = useState(false);

  function onSelectCompany(value) {
    setSelectedCompanyRadiusInfo(null);
    setSelectedCompany(value);
    setShowList(false);
    setDisplayCompaniesList(list);
    setEditChoices(false);
    setHasUpdates(false);
    onCompanySelect(value);
  }

  function onToggleList() {
    setShowList(!showList);
  }

  function onSearchCompany(v) {
    if (v === "") {
      setDisplayCompaniesList(list);
      return;
    }
    let filtered = list.filter((company) =>
      company.name.toLowerCase().includes(v.toLowerCase())
    );
    if (filtered.length > 0) {
      setDisplayCompaniesList(filtered);
    } else {
      setDisplayCompaniesList(list);
    }
  }

  function onClickOutside() {
    setTimeout(() => {
      setShowList(false);
    }, 200);
  }

  const onEditRadius = (value) => {
    setDefaultRadius(value);
  }

  const onSaveNewRadius = () => {
    let newChoice = radiusChoices.indexOf(defaultRadius) + 1;
    let formatData = {
      ...selectedCompanyRadiusInfo,
      default_radius_selection: newChoice,
    }
    if(newChoice) {
      session
        .command(
          "location/persist-default-radius-setting",
          formatData,
          {},
          { defaultErrorHandling: false }
        )
        .then(() => {
          setHasUpdates(false);
          hasChanged(true);
          loadCompanyRadius();
          setSuccessLabel("Company Default Radius Updated.");
          setShowRebuildNotice(true);
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
        });
    }
  }

  const onUpdateOptions = (newOptions) => {
    setSelectedCompanyRadiusInfo({
      ...selectedCompanyRadiusInfo,
      radius_1: newOptions.r_1,
      radius_2: newOptions.r_2,
      radius_3: newOptions.r_3,
      radius_4: newOptions.r_4,
      radius_5: newOptions.r_5,
    });
    setHasUpdates(true);
  }

  // call api after the values have changed
  useEffect(() => {
    if(hasUpdates) {
      onSaveNewRadius();
    }
    // eslint-disable-next-line
  }, [hasUpdates])

  // load radius options and default value for the company
  function loadCompanyRadius() {
    session
      .query("location/company-radius-defaults", {am_company_id: selectedCompany.id})
      .then((response) => {
        setSelectedCompanyRadiusInfo(response);
        setRadiusChoices([
          response.radius_1,
          response.radius_2,
          response.radius_3,
          response.radius_4,
          response.radius_5,
        ])
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  useEffect(() => {
    if(selectedCompany) {
      loadCompanyRadius();
    }
    // eslint-disable-next-line
  }, [selectedCompany])

  useEffect(() => {
    if(selectedCompanyRadiusInfo) {
      setTimeout(() => {
        setDefaultRadius(
          radiusChoices[selectedCompanyRadiusInfo.default_radius_selection - 1]
        );
      }, 300);
    }
    // eslint-disable-next-line
  }, [selectedCompanyRadiusInfo, radiusChoices])

  const onResetDefaults = () => {
    if(selectedCompanyRadiusInfo) {
      session
        .command(
          "location/delete-default-radius-setting",
          selectedCompanyRadiusInfo,
          {},
          { defaultErrorHandling: false }
        )
        .then(() => {
          setHasUpdates(false);
          setEditChoices(false);
          hasChanged(true);
          loadCompanyRadius();
          setSuccessLabel("Reset Default Radius Successfully.");
          setShowRebuildNotice(true);
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
        });
    }
  }

  const toggleEditOptions = () => {
    setEditChoices(!editChoices);
  }

  useEffect(() => {
    let stored_radius = 0;
    if(selectedCompanyRadiusInfo) {
      stored_radius = radiusChoices[selectedCompanyRadiusInfo.default_radius_selection - 1];
    }
    if(selectedCompanyRadiusInfo && (defaultRadius !== stored_radius)) {
      setDisableUpdate(false);
      hasChanged(false);
    } else {
      setDisableUpdate(true);
      hasChanged(true);
    }
  // eslint-disable-next-line
  }, [selectedCompanyRadiusInfo, defaultRadius])

  return (
    <div>
      <div className="dropdown-wrapper">
        <button onClick={onToggleList} className="list-btn">
          Select a Company{" "}
          {showList ? (
            <i className="bi bi-chevron-up"></i>
          ) : (
            <i className="bi bi-chevron-down"></i>
          )}
        </button>
        {showList && (
          <div className="dropdown-content">
            <input
              type="text"
              placeholder="Search by Company Name..."
              onChange={(e) => onSearchCompany(e.target.value)}
              onBlur={onClickOutside}
              autoFocus
            />
            {displayCompaniesList.map((c) => (
              <div key={c.id}>
                <button
                  className="company-btn"
                  onClick={() => onSelectCompany(c)}
                >
                  {c.name}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedCompany !== "" && (
        <div key={selectedCompany.id}>
          <br />
          <h4 className="m-3">{selectedCompany.name}:</h4>
          
          {!svgIcons && <div className="m-3 radius-editor">
            <div>
              <strong>Company Default Radius (miles)</strong>
              {editChoices ? <>
                <button className="radius-editor-btn" onClick={toggleEditOptions}>
                  <i className="bi bi-x-circle cancel-icon" /> Cancel
                </button>
                </> : 
                <button className="radius-editor-btn" onClick={toggleEditOptions}>
                  <i className="bi bi-pencil" /> <span className="edit-options-hover">Edit Options</span>
                </button>
              }
            </div>
            {
              editChoices ? 
                <EditableRadiusInput 
                  radiusChoices={radiusChoices} 
                  onUpdateOptions={onUpdateOptions}
                  onClose={() => setEditChoices(!editChoices)}
                  allowDelete={selectedCompanyRadiusInfo.id && onResetDefaults}
                  radiusOptionsChanged={hasChanged}
                /> 
              : (<>
                <div className="radius-choices">
                  <span className="radius-label">Select a Radius:</span>
                  {radiusChoices.map((choice) => 
                    <MultipleChoice 
                      key={choice}
                      value={choice} 
                      selected={defaultRadius} 
                      clicked={onEditRadius}
                    />
                  )}
                </div>
                <button 
                  type="button" 
                  className="btn-back-icons radius-btn" 
                  onClick={onSaveNewRadius}
                  disabled={disableUpdate}
                >
                  Update Radius
                </button>
                </>)              
            }
            
          </div>}
          
          {svgIcons ? (
            <ListSvgIcons
              iconList={locationType[selectedCompany.id.toString()]}
              deleteIcon={deleteLocationType}
              editSvgIcon={editLocationType}
            />
          ) : (
            <ListLocationTypes
              locationTypeList={locationType[selectedCompany.id.toString()]}
              deleteLocationType={deleteLocationType}
              editLocationType={editLocationType}
            />
          )}
        </div>
      )}
      <LocationRebuildNotice show={showRebuildNotice} label={successLabel} onClose={() => setShowRebuildNotice(false)} />
    </div>
  );
};

export default CompanySelector;


/**
 * Returns the editable list of radius inputs
 */
export const EditableRadiusInput = (props) => {
  const {radiusChoices, onUpdateOptions, onClose, radiusOptionsChanged, allowDelete = false} = props;
  const [updatedOptions, setUpdatedOptions] = useState({
    r_1: 0, r_2: 0, r_3: 0, r_4: 0, r_5: 0
  });
  const [hasChanged, setHasChanged] = useState(false);
  const max_radius = window.locations_max_radius_value || "100";

  useEffect(() => {
    if(radiusChoices.length) {
      setUpdatedOptions({
        r_1: radiusChoices[0], 
        r_2: radiusChoices[1], 
        r_3: radiusChoices[2], 
        r_4: radiusChoices[3], 
        r_5: radiusChoices[4]
      })   
    }
  }, [radiusChoices])

  const onUpdateOption = (ev) => {
    if(ev.target.value >= 1 && ev.target.value <= parseFloat(max_radius)) {
      setUpdatedOptions({...updatedOptions, [ev.target.name]: Number(ev.target.value)});
    } else if(ev.target.value < 1) {
      setUpdatedOptions({...updatedOptions, [ev.target.name]: null});
    } else if(ev.target.value > parseFloat(max_radius)) {
      setUpdatedOptions({...updatedOptions, [ev.target.name]: 100});
    }
  }

  const updateRadiusChoices = () => {
    onUpdateOptions(updatedOptions);
    onClose();
  }

  // check that the values are greater than 0
  useEffect(() => {
    let values = Object.values(updatedOptions); // collect all the values
    let _has_changes = true;
    if( values.every((value, index) => value === radiusChoices[index]) ) {
      // the same as saved values - no changes
      _has_changes = false;
    } else {
      values.forEach((value) => {
        if(value < 1 || value > max_radius) {
          _has_changes = false;
        }
        // don't let the user add repeated values as options
        let repeated = values.filter(el => el === value);
        if(repeated.length > 1) {
          _has_changes = false;
        }
      });
    }
    setHasChanged(_has_changes);
    // eslint-disable-next-line
  }, [updatedOptions])

  useEffect(() => {
    let _original = radiusChoices;
    let _updated = Object.values(updatedOptions);
    radiusOptionsChanged(isEqual(_original, _updated));
  // eslint-disable-next-line
  }, [radiusChoices, updatedOptions])

  return (
  <div>
      <input type="number" name="r_1" value={updatedOptions.r_1} onChange={onUpdateOption} min="1" max={max_radius} />
      <input type="number" name="r_2" value={updatedOptions.r_2} onChange={onUpdateOption} min="1" max={max_radius} />
      <input type="number" name="r_3" value={updatedOptions.r_3} onChange={onUpdateOption} min="1" max={max_radius} />
      <input type="number" name="r_4" value={updatedOptions.r_4} onChange={onUpdateOption} min="1" max={max_radius} />
      <input type="number" name="r_5" value={updatedOptions.r_5} onChange={onUpdateOption} min="1" max={max_radius} />
    <div>
      <div className="mb-3">
        <Alert color="light">
          <em>*Values need to be unique and between 1 and {max_radius}</em>
          </Alert>
          <Alert color="light">
          <em>**These settings will not take effect until locations are rebuilt.<br />
          Please request location rebuild from Customer Success at:</em><br />
          <b>audiencemaker@speedeondata.com</b>
        </Alert>
      </div>
      <button onClick={updateRadiusChoices} disabled={!hasChanged}>
        Update Options
      </button>
      {allowDelete && <>
        <span className="edit-or">or</span>
        <button className="radius-editor-btn" onClick={allowDelete}>
          <i className="bi bi-arrow-counterclockwise reset-icon" /> Reset All to System Default
        </button>
        </>}
    </div>
      
  </div>)
}